import React from 'react';
export const Spinner = ({ label = 'Loading…' }) => <div className="spinner">{label}</div>;
export const ErrorBox = ({ error }) => error ? <div className="alert error">{error.message || String(error)}</div> : null;
export const Success = ({ message }) => message ? <div className="alert success">{message}</div> : null;
export const fmtDate = d => { if (!d) return '—'; const s = String(d).slice(0,10); const [y,m,dd]=s.split('-'); return y&&m&&dd?`${dd}/${m}/${y}`:s; };
export const num = v => v==null||v===''?'—':Number(v).toLocaleString();

/** Money, always to the penny, so budget columns line up. */
export const money = v => v==null||v===''?'—':Number(v).toLocaleString('en-GB',{style:'currency',currency:'GBP',minimumFractionDigits:2,maximumFractionDigits:2});

const cls = s => 'state-' + String(s||'').replace(/\s/g,'');

/**
 * The automated task status. One component, so the six statuses look the
 * same everywhere they appear — stages page, reports, dashboard.
 */
export const Status = ({ value }) => value ? <span className={`badge ${cls(value)}`}>{value}</span> : <span className="muted">—</span>;
// Kept under its old name so anything still importing State keeps working.
export const State = Status;

/**
 * Complete or not, as a green tick or a red cross — the at-a-glance mark
 * on the project stages page. The symbol carries a title and an aria-label
 * as well, because colour on its own should never be the only thing
 * telling you whether a stage is done.
 */
export const Mark = ({ done, size = 15, title }) => (
  <span
    className={`mark ${done ? 'mark-done' : 'mark-not'}`}
    style={{ fontSize: size }}
    title={title || (done ? 'Complete' : 'Not complete')}
    role="img"
    aria-label={done ? 'Complete' : 'Not complete'}
  >{done ? '✓' : '✗'}</span>
);

export const Badge = ({ value }) => value ? <span className={`badge ${value}`}>{value}</span> : <span className="muted">—</span>;

/** Risk level on a project: On track / At risk / Overdue / Complete. */
export const Risk = ({ value }) => value ? <span className={`badge risk-${String(value).replace(/\s/g,'')}`}>{value}</span> : <span className="muted">—</span>;

/** Budget status: Within budget / Approaching limit / Over budget. */
export const BudgetStatus = ({ value }) => value ? <span className={`badge budget-${String(value).replace(/\s/g,'')}`}>{value}</span> : <span className="muted">—</span>;

export const Progress = ({ value }) => {
  const p = Math.max(0, Math.min(100, Number(value)||0));
  return <span title={`${p}%`}><span className={`progress${p>=100?' done':''}`}><span style={{width:`${p}%`}}/></span>
    <span style={{marginLeft:6,fontSize:12}}>{p.toFixed(0)}%</span></span>;
};

/** Budget bar — amber past the warning threshold, terracotta once over. */
export const BudgetBar = ({ pct, status }) => {
  const p = Math.max(0, Math.min(100, Number(pct)||0));
  const tone = status === 'Over budget' ? ' over' : status === 'Approaching limit' ? ' soon' : '';
  return <span title={`${Number(pct)||0}% of budget spent`}>
    <span className={`progress budget${tone}`}><span style={{width:`${p}%`}}/></span>
    <span style={{marginLeft:6,fontSize:12}}>{pct==null?'—':`${Number(pct).toFixed(0)}%`}</span></span>;
};

export const PrintHeader = ({ title, subtitle, generatedAt, generatedBy }) => (
  <div className="report-header">
    <h1>{title}</h1>
    <div className="muted">{subtitle ? subtitle+' | ' : ''}Generated {generatedAt ? new Date(generatedAt).toLocaleString('en-GB') : ''}{generatedBy ? ' by '+generatedBy : ''} | Building Safety Programme Tracker</div>
  </div>
);

export const DataTable = ({ columns, rows, empty = 'No rows' }) => {
  if (!rows || !rows.length) return <p className="muted">{empty}</p>;
  return <div className="table-wrap"><table className="data"><thead><tr>
    {columns.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead><tbody>
    {rows.map((r,i) => <tr key={r.id ?? i}>{columns.map(c => <td key={c.key}>{c.render ? c.render(r[c.key], r) : (r[c.key] ?? '—')}</td>)}</tr>)}
    </tbody></table></div>;
};

/**
 * A plain dropdown menu — a button that opens a list — rather than a
 * native select box. The report picker needs headings over each group and
 * a line of description under each title, neither of which a select box
 * can show.
 */
export function DropdownMenu({ label, value, groups, onChange, width = 360 }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  React.useEffect(() => {
    const away = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', away);
    document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', away); document.removeEventListener('keydown', esc); };
  }, []);
  const all = groups.flatMap(g => g.items);
  const current = all.find(i => i.value === value);
  return (
    <div className="dropdown" ref={ref} style={{ width }}>
      {label && <label>{label}</label>}
      <button type="button" className="dropdown-toggle" onClick={() => setOpen(o => !o)}
        aria-haspopup="menu" aria-expanded={open}>
        <span className="dropdown-current">{current ? current.label : 'Choose…'}</span>
        <span className="caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="dropdown-menu" role="menu">
          {groups.map(g => (
            <div key={g.label}>
              <div className="dropdown-group">{g.label}</div>
              {g.items.map(i => (
                <button key={i.value} type="button" role="menuitem"
                  className={`dropdown-item${i.value === value ? ' selected' : ''}`}
                  onClick={() => { onChange(i.value); setOpen(false); }}>
                  <span className="dropdown-item-label">{i.label}</span>
                  {i.hint && <span className="dropdown-item-hint">{i.hint}</span>}
                </button>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
